/**
 * Module for parsing URLs and resolving SearchParams from queryKey for sidepanel listings.
 */
import {getDefaultJellyfishSearchParams} from 'app-core/archetype/JellyfishSearchParams';
import {decodeJQueryParam} from 'BenchlingUtil';
import * as FileBrowserConstants from 'constants/FileBrowserConstants';
import {NumberIDFolder} from 'constants/FolderConstants';
import {SourceSummary} from 'constants/SourceConstants';
import * as FileBrowserDAO from 'file-browser/FileBrowserDAO';
import FinderUtil from 'file-browser/FinderUtil';
import FolderDAO from 'file-browser/FolderDAO';
import {getDefaultAnalyticsSearchParams} from 'insights/sidepanel/getDefaultAnalyticsSearchParams';
import {
  sanitizeProjectParamsForGranularPermissions,
  translateCompositeToSightglass,
} from 'nav/sidepanel/listing/ProjectsListingFilters';
import getDefaultLibrarySearchParams from 'notebook/library/library-sidepanel/getDefaultLibrarySearchParams';
import * as RegistryUtil from 'registry/RegistryUtil';
import * as FilterUtil from 'search/FilterUtil';
import {Filter, FilterKey, keyToEmptyFilter, SearchParams, SingleValueFilter} from 'search/SearchConstants';
import {getDefaultStudiesSearchParams} from 'studies/workspace/StudySearchHelpers';
import {WORKFLOW_FILTER_ANY} from 'workflows/WorkflowConstants';
import {getDefaultWorkflowsSearchParams} from 'workflows/WorkflowsSearchParams';
import {
  getDefaultWorklistItemsSearchParams,
  getDefaultWorklistSearchParams,
} from 'worklists/WorklistSearchParams';

import {getDefaultFolderSearchParams} from './FolderSearchParams';
import {
  getDefaultProjectSearchParams,
  getDefaultTypesForSection,
  resolveFolderFilter,
} from './ProjectsSearchParams';
import {getDefaultSearchSearchParams} from './SearchSearchParams';
import {QueryKey, searchParamsFromQueryKey} from './SearchURLs';
import {
  getDefaultStorablesSearchParams,
  hasQueryOrNonDefaultStorablesFilters,
  LIST_ONLY_DEFAULT_FILTER_KEYS,
} from './StorablesSearchParams';

export interface LegacyWorkflowQueryKey {
  status: string;
  templateId: string;
  authorId: string;
}

/**
 * Parses filters from two forms of URLs:
 * - Legacy listing: /workflows?status=ANY&templateId=ANY&authorId=ANY
 * - Nav V3 listing: /workflows?filter=workflowStatus%3ADRAFT&offset=0&limit=25&sort=name&reverse=0&q=
 */
export function parseWorkflowsURL(queryKey: LegacyWorkflowQueryKey & QueryKey): SearchParams {
  const status = queryKey.status ? decodeJQueryParam(queryKey.status) : null;
  const templateId = queryKey.templateId ? decodeJQueryParam(queryKey.templateId) : null;
  const authorId = queryKey.authorId ? decodeJQueryParam(queryKey.authorId) : null;
  const querySearchParams = searchParamsFromQueryKey(queryKey);
  const defaultSearchParams = getDefaultWorkflowsSearchParams();
  const filters = [];
  if (status && status !== WORKFLOW_FILTER_ANY) {
    filters.push({...keyToEmptyFilter(FilterKey.WORKFLOW_STATUS), value: status});
  }
  if (templateId && templateId !== WORKFLOW_FILTER_ANY) {
    filters.push({...keyToEmptyFilter(FilterKey.WORKFLOW_TEMPLATE), value: templateId});
  }
  if (authorId && authorId !== WORKFLOW_FILTER_ANY) {
    filters.push({...keyToEmptyFilter(FilterKey.AUTHOR), value: [authorId]});
  }
  return {
    ...defaultSearchParams,
    // Apply any params from new URL format
    ...querySearchParams,
    // Apply any filters from the old URL format
    filters: FilterUtil.replaceByKey(
      defaultSearchParams.filters,
      ...filters,
      ...(querySearchParams.filters || [])
    ),
  };
}

interface StorablesURLData {
  searchParams: SearchParams;
  registry: SourceSummary;
}

export async function parseStorablesURL(queryKey: QueryKey): Promise<StorablesURLData | null> {
  const querySearchParams = searchParamsFromQueryKey(queryKey);
  // If there's no registry filter, it's not a valid listing, so return null.
  if (
    !querySearchParams.filters ||
    !FilterUtil.getFilterByKey(querySearchParams.filters, FilterKey.REGISTRY)
  ) {
    return null;
  }
  const registryId = FilterUtil.getFilterValueByKey(querySearchParams.filters, FilterKey.REGISTRY);
  const registry = await FileBrowserDAO.fetchSource(registryId as string);
  const defaultSearchParams = getDefaultStorablesSearchParams(registry!.id);
  return {
    searchParams: {
      ...defaultSearchParams,
      ...querySearchParams,
      filters: getStorablesFilters(defaultSearchParams, querySearchParams),
    },
    registry: registry!,
  };
}

function getStorablesFilters(
  defaultSearchParams: SearchParams,
  querySearchParams: Partial<SearchParams>
): Array<Filter> {
  let defaultSearchFilters = [...defaultSearchParams.filters];
  const hasQueryOrNonDefaultFilters = hasQueryOrNonDefaultStorablesFilters({
    ...defaultSearchParams, // Missing filters are ok and will be added later, so start with defaults.
    ...querySearchParams,
  });
  if (hasQueryOrNonDefaultFilters) {
    // Remove filter keys meant only for initial listing of storables
    defaultSearchFilters = defaultSearchFilters.filter(
      (filter) => !LIST_ONLY_DEFAULT_FILTER_KEYS.includes(filter.key)
    );
  }
  return FilterUtil.replaceByKey(defaultSearchFilters, ...(querySearchParams.filters || []));
}

interface FolderURLData {
  searchParams: SearchParams;
  folder: NumberIDFolder | null;
}

export interface LegacyFolderURLQueryKey {
  batchSchema?: string;
  collection?: string;
  scope?: string;
  section?: string;
  storableSchema?: string;
  tagSchema?: string;
}

/**
 * Parses first-class Nav V2 query key params into Nav V3 filters..
 */
function parseLegacyFolderQueryKey(queryKey: LegacyFolderURLQueryKey): Array<Filter> {
  const filters = [];
  const schemaId = queryKey.tagSchema || queryKey.batchSchema || queryKey.storableSchema;
  if (schemaId) {
    filters.push({
      ...keyToEmptyFilter(FilterKey.SCHEMA),
      value: [schemaId],
    });
  }
  if (queryKey.section) {
    let section = queryKey.section;
    if (queryKey.section === FileBrowserConstants.ALL_SECTION) {
      section = FinderUtil.getDefaultSection();
    }
    filters.push({
      ...keyToEmptyFilter(FilterKey.FOLDER_SECTION),
      value: section,
    });
    filters.push({
      ...keyToEmptyFilter(FilterKey.TYPES),
      value: getDefaultTypesForSection(section),
    });
  }
  if (queryKey.collection) {
    filters.push({
      ...keyToEmptyFilter(FilterKey.REGISTRY_COLLECTION_OWNER),
      value: queryKey.collection,
    });
  }
  if (queryKey.scope) {
    filters.push({
      ...keyToEmptyFilter(FilterKey.ANCESTOR_FOLDER),
      value: queryKey.scope,
    });
  }
  return filters;
}

export async function parseFolderURL(
  folderId: string,
  queryKey: LegacyFolderURLQueryKey & QueryKey
): Promise<FolderURLData> {
  const folder = await FolderDAO.fetch(folderId, {summary: '1'});
  // Apply the base params.
  // @ts-expect-error
  const defaultSearchParams = getDefaultFolderSearchParams(folder);
  // Override with params from the query key.
  const querySearchParams = searchParamsFromQueryKey(queryKey);
  // Override with params from legacy V2 params if present.
  const filters = parseLegacyFolderQueryKey(queryKey);
  const searchParams = {
    ...defaultSearchParams,
    ...querySearchParams,
    filters: FilterUtil.replaceByKey(
      defaultSearchParams.filters,
      ...(querySearchParams.filters || []),
      ...filters
    ),
  };
  return {
    searchParams: RegistryUtil.isRegistryFolder(folder!) ? searchParams : resolveFolderFilter(searchParams),
    folder: folder!,
  };
}

export function parseSearchURL(queryKey: LegacyFolderURLQueryKey & QueryKey): SearchParams | null {
  const querySearchParams = searchParamsFromQueryKey(queryKey);
  const defaultSearchParams = getDefaultSearchSearchParams();
  const filters = parseLegacyFolderQueryKey(queryKey);
  const searchParams = {
    ...defaultSearchParams,
    ...querySearchParams,
    filters: FilterUtil.replaceByKey(
      defaultSearchParams.filters,
      ...(querySearchParams.filters || []),
      ...filters
    ),
  };
  return searchParams;
}

export function parseProjectsURL(queryKey: LegacyFolderURLQueryKey & QueryKey): SearchParams | null {
  const querySearchParams = searchParamsFromQueryKey(queryKey);
  // If no query, show projects listing.
  if (querySearchParams.query == null) {
    return null;
  }
  const defaultSearchParams = getDefaultProjectSearchParams();
  const filters = parseLegacyFolderQueryKey(queryKey);
  const searchParams = sanitizeProjectParamsForGranularPermissions({
    ...defaultSearchParams,
    ...querySearchParams,
    filters: FilterUtil.replaceByKey(
      translateCompositeToSightglass(defaultSearchParams.filters),
      ...(querySearchParams.filters || []),
      ...filters
    ),
  });
  return searchParams;
}

export function parseWorklistsURL(queryKey: QueryKey): SearchParams {
  const querySearchParams = searchParamsFromQueryKey(queryKey);
  const defaultSearchParams = getDefaultWorklistSearchParams();
  const searchParams = {
    ...defaultSearchParams,
    ...querySearchParams,
    filters: FilterUtil.replaceByKey(defaultSearchParams.filters, ...(querySearchParams.filters || [])),
  };
  return searchParams;
}

/**
 * Extract searchParams from URL, returning null worklistId if ITEM_IN_WORKLIST filter is not provided
 */
export function parseWorklistItemsURL(queryKey: QueryKey): {
  searchParams: SearchParams;
  worklistId: string | null;
} {
  const querySearchParams = searchParamsFromQueryKey(queryKey);
  const itemInWorklistFilter = (
    querySearchParams.filters
      ? FilterUtil.getFilterByKey(querySearchParams.filters, FilterKey.ITEM_IN_WORKLIST)
      : null
  ) as SingleValueFilter | null;

  const worklistId = itemInWorklistFilter ? itemInWorklistFilter.value : null;

  const defaultSearchParams = getDefaultWorklistItemsSearchParams();
  const searchParams = {
    ...defaultSearchParams,
    ...querySearchParams,
    filters: FilterUtil.replaceByKey(defaultSearchParams.filters, ...(querySearchParams.filters || [])),
  };

  return {searchParams, worklistId};
}

export function parseAnalyticsURL(queryKey: QueryKey): SearchParams {
  const querySearchParams = searchParamsFromQueryKey(queryKey);
  const defaultAnalyticsSearchParams = getDefaultAnalyticsSearchParams();
  const searchParams = {
    ...defaultAnalyticsSearchParams,
    ...querySearchParams,
    filters: FilterUtil.replaceByKey(
      defaultAnalyticsSearchParams.filters,
      ...(querySearchParams.filters || [])
    ),
  };
  return searchParams;
}

export function parseJellyfishURL(queryKey: QueryKey): SearchParams {
  const querySearchParams = searchParamsFromQueryKey(queryKey);
  const defaultJellyfishSearchParams = getDefaultJellyfishSearchParams();
  const searchParams = {
    ...defaultJellyfishSearchParams,
    ...querySearchParams,
    filters: FilterUtil.replaceByKey(
      defaultJellyfishSearchParams.filters,
      ...(querySearchParams.filters || [])
    ),
  };
  return searchParams;
}

export function parseTemplateCollectionURL(queryKey: QueryKey): {
  searchParams: SearchParams;
  templateCollectionId: string | null;
} {
  const querySearchParams = searchParamsFromQueryKey(queryKey);
  const templateCollectionId = querySearchParams.filters
    ? (FilterUtil.getFilterValueByKey(querySearchParams.filters, FilterKey.TEMPLATE_COLLECTION) as
        | string
        | null)
    : null;
  const defaultLibrarySearchParams = getDefaultLibrarySearchParams();
  const searchParams = {
    ...defaultLibrarySearchParams,
    ...querySearchParams,
    filters: FilterUtil.replaceByKey(
      defaultLibrarySearchParams.filters,
      ...(querySearchParams.filters || [])
    ),
  };
  return {searchParams, templateCollectionId};
}

export function parseStudiesURL(queryKey: QueryKey): SearchParams {
  const querySearchParams = searchParamsFromQueryKey(queryKey);
  const defaultStudiesSearchParams = getDefaultStudiesSearchParams();
  const searchParams = {
    ...defaultStudiesSearchParams,
    ...querySearchParams,
    filters: FilterUtil.replaceByKey(
      defaultStudiesSearchParams.filters,
      ...(querySearchParams.filters || [])
    ),
  };
  return searchParams;
}
